/* eslint-disable react/no-array-index-key */
/* eslint-disable react/require-default-props */
import React, {
   useEffect,
   useRef,
   useState,
   useMemo,
   forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from '../../services/axios';
import InputClearButton from '../MinorUtilities/InputClearButton/InputClearButton';
import StatusBadge from '../StatusBadge';
import employmentStatusBadge from './employmentStatus';
import rankSuggestions from './rankSuggestions';
import { OptionButton } from '../../styles/ui/Button';

// =============================================================================
// Estilos — idênticos ao ProductNameSearch para consistência visual
// =============================================================================

const Wrap = styled.div`
   position: relative;
`;

const InputBox = styled.div`
   position: relative;
`;

const Input = styled.input`
   width: 100%;
   padding-right: 48px;
`;

const Menu = styled.div`
   position: absolute;
   left: 0;
   right: 0;
   top: calc(100% + 6px);
   background: #fff;
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 12px;
   overflow: hidden;
   z-index: 40;
   box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
   max-height: 320px;
   overflow-y: auto;
`;

const Row = styled(OptionButton)`
   font-size: 0.75rem;
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

// Nome e situação dividem a primeira linha: a badge fica ancorada à direita,
// na mesma coluna em todas as linhas, para a leitura vertical resolver o caso
// de homônimo sem o operador ter de caçar o status no meio do texto.
const RowTop = styled.span`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   width: 100%;
`;

const RowName = styled.span`
   font-weight: 500;
   font-size: 0.8rem;
   min-width: 0;
`;

const RowMeta = styled.span`
   font-size: 0.7rem;
   color: #9ca3af;
`;

const InfoRow = styled.div`
   width: 100%;
   padding: 10px 12px;
   font-size: 0.75rem;
   color: #373737;
   opacity: 0.75;
`;

const Hint = styled.div`
   margin-top: 6px;
   font-size: 12px;
   opacity: 0.75;
`;

// =============================================================================
// Helpers
// =============================================================================

function escapeRegExp(str) {
   return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderHighlighted(text, term) {
   const t = String(term || '').trim();
   if (!t) return text;

   const re = new RegExp(`(${escapeRegExp(t)})`, 'ig');
   const parts = String(text || '').split(re);

   return parts.map((part, index) =>
      index % 2 === 1 ? (
         <strong key={`${index}-${part}`}>{part}</strong>
      ) : (
         <span key={`${index}-${part}`}>{part}</span>
      ),
   );
}

// =============================================================================
// Componente
// =============================================================================

/**
 * Autocomplete de funcionário — busca por nome via GET /hr/employees.
 *
 * Props:
 *   value           {string}   — texto exibido no input (controlado pelo pai)
 *   onChange        {fn}       — chamado com o novo texto enquanto o usuário digita
 *   onSelect        {fn}       — chamado com o objeto employee selecionado (ou null ao limpar)
 *   initialSelected {object?}  — sinaliza que o `value` inicial JÁ é uma seleção
 *                                (não digitação). Inicializa isLocked=true e
 *                                pré-popula selectedRef com o employee, evitando
 *                                que a busca dispare ao montar o componente já
 *                                com um valor pré-preenchido.
 *   required        {boolean}  — se true, ao perder foco sem seleção válida, limpa o campo
 *   placeholder     {string}
 *   minChars        {number}   — mínimo de caracteres para disparar a busca (default 3)
 *   debounceMs      {number}   — debounce em ms (default 300)
 *   limit           {number}   — máximo de sugestões (default 30)
 *   disabled        {boolean}
 *   showHint        {boolean}  — exibe texto de dica abaixo do input
 */
const EmployeeNameSearch = forwardRef(function EmployeeNameSearch(
   {
      value,
      onChange,
      onSelect,
      initialSelected = null,
      required = false,
      placeholder = 'Buscar funcionário por nome...',
      minChars = 3,
      debounceMs = 300,
      // 30 e não 15 (2026-09-11): a ordenação por proximidade/situação
      // (rankSuggestions) só reorganiza o que o backend devolveu — quem ficou
      // fora do corte não é resgatado por ordenação nenhuma. Uma janela maior
      // reduz a chance de a pessoa certa cair fora em base com muitos
      // homônimos. O corte correto é backend-first (ordenar antes do LIMIT).
      limit = 30,
      disabled = false,
      showHint = false,
      extraParams = {},
   },
   ref,
) {
   const [suggestions, setSuggestions]   = useState([]);
   const [isMenuOpen, setIsMenuOpen]     = useState(false);
   const [isLoading, setIsLoading]       = useState(false);
   const [activeIndex, setActiveIndex]   = useState(-1);
   // Quando o pai passa initialSelected, o value inicial é uma seleção válida —
   // travamos a busca de cara pra evitar que `value.length >= minChars` dispare
   // o autocomplete ao montar.
   const [isLocked, setIsLocked]         = useState(!!initialSelected);

   const requestIdRef   = useRef(0);
   const selectedRef    = useRef(initialSelected ?? null);
   const inputRef       = useRef(null);

   // Expor ref externo
   useEffect(() => {
      if (!ref) return;
      if (typeof ref === 'function') ref(inputRef.current);
      else ref.current = inputRef.current; // eslint-disable-line no-param-reassign
   }, [ref]);

   // Desbloquear quando o valor for limpo externamente
   useEffect(() => {
      if ((value ?? '') === '') {
         setIsLocked(false);
         selectedRef.current = null;
      }
   }, [value]);

   const trimmedQuery = useMemo(() => (value ?? '').trim(), [value]);
   const shouldSearch = !isLocked && trimmedQuery.length >= minChars;

   // Chave estável para extraParams — evita loop de re-render quando o
   // callsite passa um objeto inline. Memoizar pela forma serializada
   // garante que só re-buscamos quando o conteúdo muda de fato.
   const extraParamsKey = useMemo(
      () => JSON.stringify(extraParams ?? {}),
      [extraParams]
   );

   // ─── Limpar seleção ────────────────────────────────────────────
   const handleClear = () => {
      requestIdRef.current += 1;
      selectedRef.current = null;
      setIsLocked(false);
      setIsMenuOpen(false);
      setSuggestions([]);
      setActiveIndex(-1);
      onSelect?.(null);
      onChange?.('');
      setTimeout(() => inputRef.current?.focus(), 0);
   };

   // ─── Selecionar um funcionário da lista ───────────────────────
   const pickEmployee = (employee) => {
      requestIdRef.current += 1;
      selectedRef.current = employee;
      setIsLocked(true);
      setIsMenuOpen(false);
      setSuggestions([]);
      setActiveIndex(-1);
      onChange?.(employee.full_name);
      onSelect?.(employee);
   };

   // ─── Digitar no input ──────────────────────────────────────────
   const handleInputChange = (event) => {
      const nextValue = event.target.value;

      if (isLocked) {
         selectedRef.current = null;
         setIsLocked(false);
         onSelect?.(null);
      }

      onChange?.(nextValue);
   };

   // ─── Busca com debounce ────────────────────────────────────────
   useEffect(() => {
      if (disabled || !shouldSearch) {
         setIsLoading(false);
         setIsMenuOpen(false);
         setSuggestions([]);
         setActiveIndex(-1);
         return;
      }

      const currentRequestId = ++requestIdRef.current; // eslint-disable-line no-plusplus

      setIsLoading(true);
      setIsMenuOpen(true);
      setSuggestions([]);
      setActiveIndex(-1);

      const debounceTimer = setTimeout(async () => {
         try {
            const response = await axios.get('/hr/employees', {
               params: { name: trimmedQuery, limit, page: 1, ...extraParams },
            });

            if (currentRequestId !== requestIdRef.current) return;

            const employeeList = response.data?.data?.employees ?? [];
            setSuggestions(rankSuggestions(employeeList, trimmedQuery));
            setIsMenuOpen(true);
            setActiveIndex(-1);
         } catch {
            if (currentRequestId !== requestIdRef.current) return;
            setSuggestions([]);
            setIsMenuOpen(false);
         } finally {
            if (currentRequestId === requestIdRef.current) setIsLoading(false);
         }
      }, debounceMs);

      return () => clearTimeout(debounceTimer); // eslint-disable-line consistent-return
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [trimmedQuery, debounceMs, limit, disabled, shouldSearch, extraParamsKey]);

   // ─── Navegação por teclado ─────────────────────────────────────
   const handleKeyDown = (event) => {
      if (!isMenuOpen || !suggestions.length) return;

      if (event.key === 'ArrowDown') {
         event.preventDefault();
         setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
      }

      if (event.key === 'ArrowUp') {
         event.preventDefault();
         setActiveIndex((i) => Math.max(i - 1, 0));
      }

      if (event.key === 'Enter') {
         event.preventDefault();
         if (activeIndex >= 0 && suggestions[activeIndex]) {
            pickEmployee(suggestions[activeIndex]);
         }
      }

      if (event.key === 'Escape') setIsMenuOpen(false);
   };

   const handleFocus = () => {
      if (!isLocked && (suggestions.length || isLoading)) setIsMenuOpen(true);
   };

   const handleBlur = () => {
      setTimeout(() => {
         setIsMenuOpen(false);

         // Se required e não há seleção válida, limpa o campo
         if (required && !isLocked && (value ?? '').trim()) {
            handleClear();
         }
      }, 120);
   };

   const showClearButton = !disabled && (value ?? '') !== '';


   return (
      <Wrap>
         <InputBox>
            <Input
               ref={inputRef}
               value={value ?? ''}
               placeholder={placeholder}
               onChange={handleInputChange}
               onKeyDown={handleKeyDown}
               onFocus={handleFocus}
               onBlur={handleBlur}
               disabled={disabled}
               autoComplete="off"
            />

            <InputClearButton
               visible={showClearButton}
               side="right"
               hoverText="Limpar"
               onClear={handleClear}
               disabled={disabled}
            />

            {isMenuOpen && (
               <Menu>
                  {isLoading && <InfoRow>Carregando…</InfoRow>}

                  {!isLoading && suggestions.length === 0 && trimmedQuery && (
                     <InfoRow>Nenhum funcionário encontrado.</InfoRow>
                  )}

                  {!isLoading &&
                     suggestions.map((employee, index) => {
                        const status = employmentStatusBadge(
                           employee.employment_status
                        );
                        return (
                           <Row
                              key={employee.id ?? index}
                              type="button"
                              $active={index === activeIndex}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => pickEmployee(employee)}
                           >
                              <RowTop>
                                 <RowName>
                                    {renderHighlighted(
                                       employee.full_name,
                                       trimmedQuery
                                    )}
                                 </RowName>
                                 {status && (
                                    <StatusBadge
                                       variant={status.variant}
                                       size="sm"
                                    >
                                       {status.label}
                                    </StatusBadge>
                                 )}
                              </RowTop>
                              <RowMeta>
                                 {employee.jobPosition?.position_name ?? ''}
                                 {employee.department?.department_name
                                    ? ` · ${employee.department.department_name}`
                                    : ''}
                                 {employee.city_location
                                    ? ` · ${employee.city_location}`
                                    : ''}
                              </RowMeta>
                           </Row>
                        );
                     })}
               </Menu>
            )}
         </InputBox>

         {showHint && (
            <Hint>
               {required
                  ? 'Digite o nome e selecione um funcionário da lista.'
                  : 'Digite parte do nome para sugerir funcionários.'}
            </Hint>
         )}
      </Wrap>
   );
});

EmployeeNameSearch.propTypes = {
   value:           PropTypes.string,
   onChange:        PropTypes.func,
   onSelect:        PropTypes.func,
   // eslint-disable-next-line react/forbid-prop-types
   initialSelected: PropTypes.object,
   required:        PropTypes.bool,
   placeholder:     PropTypes.string,
   minChars:        PropTypes.number,
   debounceMs:      PropTypes.number,
   limit:           PropTypes.number,
   disabled:        PropTypes.bool,
   showHint:        PropTypes.bool,
   // eslint-disable-next-line react/forbid-prop-types
   extraParams:     PropTypes.object,
};

export default EmployeeNameSearch;
